import axios, { AxiosResponse } from 'axios';
import { getCountryStates } from '@ikea-customer-self-serve/contact-form';
import { Errors } from './errors';
import {
  LocationServiceResponse,
  PostalCodeLocation,
  GoogleGeocodeAPIResponse,
  AddressComponents,
  PostalCodeLookup,
} from './model';

/* global CSS_CONFIG */
const {
  COUNTRY,
  GETSTATES: { useStateNameAbbreviation, ingkaLocationServiceApi, apiSrc },
  DEFAULTLANGUAGE,
  LANGUAGE,
  SERVICES: { getServicesUsingStateCode },
} = CSS_CONFIG;

class State {
  private getFallbackLocations(): PostalCodeLocation[] {
    // Populate with fallback locations for countries requiring a state code
    // in get services request.
    return getServicesUsingStateCode
      ? getCountryStates(COUNTRY).map((state) => {
          return { state };
        })
      : [];
  }

  private async getStateByLocationServiceAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const result: AxiosResponse<LocationServiceResponse> = await axios.get(
        `${ingkaLocationServiceApi}/locationservice/${COUNTRY}/${postalCode}`,
      );
      let locations: PostalCodeLocation[] = [];

      if (COUNTRY === 'au') {
        const { additionalAttributes } = result.data;
        locations = (additionalAttributes?.stateSuburbRelation || []).map(
          (stateSuburb) => {
            return { state: stateSuburb.state, city: stateSuburb.city };
          },
        );
        return { locations, valid: true };
      }

      const { additionalAttributes } = result.data;
      locations = (additionalAttributes?.stateInfo || []).map((info) => {
        const state = useStateNameAbbreviation
          ? info.stateCode
          : info.stateName || info.stateCode;
        return { state };
      });

      // Invalid postal code case 1.
      // US returns status 200 and validZipCode set to false for invalid postal code.
      const { validZipCode } = result.data;
      const valid = validZipCode !== undefined ? validZipCode : true;

      return { locations, valid };
    } catch (err) {
      const { response } = err;

      // Invalid postal code case 2.
      // AU and CA returns status 400 for invalid postal code.
      if (response?.status === Errors.INVALID_POSTAL_CODE) {
        return { locations: [], valid: false };
      }

      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  private async getStateByGoogleGeocodeAPI(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    try {
      const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${postalCode}&key=${GOOGLE_API_KEY}&region=${COUNTRY}&language=${
        DEFAULTLANGUAGE || LANGUAGE
      }`;
      const result: AxiosResponse<GoogleGeocodeAPIResponse> =
        await axios.get(url);
      const addressComponents = result.data.results[0].address_components;
      const [localArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some((type: string) => type === 'locality'),
      );
      const [stateArray] = (addressComponents || []).filter(
        (item: AddressComponents) =>
          item.types.some(
            (type: string) => type === 'administrative_area_level_1',
          ),
      );

      const city = localArray?.long_name;
      const state = useStateNameAbbreviation
        ? stateArray.short_name
        : stateArray.long_name;

      return state
        ? { locations: [{ city, state }], valid: true }
        : { locations: this.getFallbackLocations(), valid: true };
    } catch (err) {
      return { locations: this.getFallbackLocations(), valid: true };
    }
  }

  public async getPostalCodeLocations(
    postalCode: string,
  ): Promise<PostalCodeLookup> {
    if (apiSrc === 'GOOGLE') {
      return this.getStateByGoogleGeocodeAPI(postalCode);
    }

    if (apiSrc === 'INGKA_LOCATION') {
      return this.getStateByLocationServiceAPI(postalCode);
    }

    return Promise.resolve({ locations: [], valid: true });
  }
}

export default State;
